import { select, put, call, delay } from 'redux-saga/effects';
import { chunk } from 'lodash';
import i18n from 'i18next';
import { ee } from '../../../../services/earth-engine';
import { callback, evaluate } from '../../../tools/effects';
import { Actions as Dialog } from '../../dialog';
import { Actions as Snacks } from '../../snacks';
import { Actions as Common } from '../../common';
import { Actions as Results } from '../../results';
import { Actions as Map } from '../../map';
import { Actions as Imagery } from '../header';
import { openAndWait } from '../../dialog/saga';
import * as Selectors from '../../../../selectors';
import { Timer, generateColors } from '../../../../common/utils';
import * as Metadata from '../../../../common/metadata';
import * as Coastline from '../../../../algorithms/shoreline';

export class ResultsCacheHolder {
  static shorelines = null;
  static transacts = null;
}

/**
 * @function deriveInChunks
 * @description Processes selected satellite images in chunks to derive shoreline data with progress tracking.
 * @param {Array<Object>} selectedImages - Array of date objects containing image information
 * @param {Object} satellite - Satellite configuration object
 * @param {Object} roi - Region of interest (ROI) geometry
 * @param {number} bufDistance - Buffer distance in meters
 * @param {number} threshold - Threshold value for shoreline detection
 * @param {Array} transects - Transects array for analysis
 * @param {Function} [acquireImage] - Optional (mission, date, geometry) => ee.Image
 *   override forwarded to `Coastline.deriveShorelines` — see that function's
 *   doc comment for why day-only date callers (Shore-S) need to pass one.
 *
 * @generator
 * @yields {Object} Snacks.task - Initial progress notification
 * @yields {Object} Snacks.update - Progress updates with status and percentage
 * @yields {Object} Snacks.success - Success notification on completion
 * @yields {Object} Snacks.error - Error notification on failure
 * @yields {Object} Snacks.dismiss - Dismisses notifications (multiple times)
 * @yields {Object} Results.push - Adds processed image timestamps to state
 * @yields {Object} HIDE_LOADING_MESSAGE - Hides loading indicator
 * @yields {Object} SHORELINE_CHUNK_ERROR - Logs chunk processing errors
 * @yields {Promise} call(Coastline.deriveShorelines) - Derives shorelines from GEE
 * @yields {Promise} callback([job, job.evaluate]) - Evaluates Earth Engine data
 * @yields {Promise} delay(10) - Pre-chunk processing delay
 * @yields {Promise} delay(500) - Retry delay on error
 * @yields {Promise} delay(3000) - Success message display time
 * @yields {Promise} delay(5000) - Error message display time
 * @see {@link performAnalysis}
 * **Called by:
 * @see {performAnalysis} - Shoreline module's coastline analysis workflow.
 * @see {performShoreRAnalysis} - ShoreR module's coastline analysis workflow.
 * @memberof module:SHARED
 * @returns {Array<Object>} Array of evaluated shoreline features
 */
export const deriveInChunks = function* (
  selectedImages,
  satellite,
  roi,
  bufDistance,
  threshold,
  transects,
  acquireImage,
) {
  try {
    const imageDates = selectedImages.map((el) => ({
      date: el.date,
      dateFormated: new Date(el.date).toISOString(),
    }));
    window.sessionStorage.setItem('ImageDates', JSON.stringify(imageDates));
    window.sessionStorage.setItem('shorelineImagesErrors', JSON.stringify([]));
  } catch (storageError) {
    console.warn('[Storage] Failed to save to sessionStorage:', storageError);
  }
  const length = selectedImages.length;
  const size = Math.max(1, Math.min(Math.ceil(length / 5), 10));
  /**
   * What the user reads while this runs. The title names the job once; the detail is the part that
   * moves, so the indicator does not rewrite its own heading on every chunk.
   */
  const progress = (step, etc = '') =>
    `${step}/${length}${etc ? ` [${i18n.t('forms.shorelineAnalysis.process.estimate')} ${etc}]` : ''}`;
  const title = i18n.t('forms.shorelineAnalysis.process.extraction');

  yield put(Common.notice(title, progress(0)));
  const evaluated = [];
  const chunks = chunk(selectedImages, size);
  const timer = new Timer();
  const MAX_RETRIES = 1;

  const getStatus = () => progress(evaluated.length, timer.format(timer.project(evaluated.length, length)));
  const getLoadProgress = () => ({
    variant: 'determinate',
    value: (evaluated.length / length) * 100,
  });

  for (let chunkIndex = 0; chunkIndex < chunks.length; chunkIndex++) {
    const currentChunk = chunks[chunkIndex];
    const chunkNumber = chunkIndex + 1;
    let chuckProcessed = false;
    let lastError = null;
    for (let attempt = 0; attempt < MAX_RETRIES; attempt++) {
      try {
        yield delay(10);
        const shorelines = yield call(
          Coastline.deriveShorelines,
          currentChunk,
          satellite,
          roi,
          bufDistance,
          threshold,
          transects,
          acquireImage,
        );
        if (!shorelines || shorelines.length === 0) continue;
        const validShorelines = shorelines.filter((el) => el !== null);

        const job = ee.List(shorelines);

        const data = yield callback([job, job.evaluate]);
        const currentEvaluated = yield select((state) => state.results.evaluatedImages || []);
        yield put(
          Results.push('evaluatedImages', [
            ...currentEvaluated,
            ...data.map((el) => el.properties['system:time_start']),
          ]),
        );
        evaluated.push(...data);
        chuckProcessed = true;
        yield put(Common.notice(title, getStatus()));
        break;
      } catch (e) {
        console.error(
          `[Shoreline] Chunk ${chunkNumber}, Attempt ${attempt + 1}/${MAX_RETRIES} failed:`,
          e.message || e,
        );
        if (attempt < MAX_RETRIES - 1) {
          yield delay(500);
        }
      }
    }
    if (!chuckProcessed) {
      yield put({ type: 'HIDE_LOADING_MESSAGE' });
      const errorMessage =
        `The Google Earth Engine (GEE) was not able to complete processing chunk ${chunkNumber}.\n` +
        `You may try again later.\n` +
        `Technical error: ${lastError?.message || lastError}`;

      console.error(errorMessage);

      yield put({
        type: 'SHORELINE_CHUNK_ERROR',
        payload: {
          chunkNumber,
          error: lastError?.message || String(lastError),
          timestamp: Date.now(),
        },
      });
    }
  }

  if (evaluated.length > 0) {
    yield put(Snacks.success('shore-ext-success', 'Shorelines derived'));
    yield delay(3000);
    yield put(Snacks.dismiss('shore-ext-success'));
    yield put(Common.clearNotice());
    return ee.FeatureCollection(evaluated);
  }
  yield put(Snacks.error('shore-ext-error', 'No shorelines could be derived'));
  yield put(Common.clearNotice());
  yield delay(5000);
  yield put(Snacks.dismiss('shore-ext-error'));
  return null;
};

/**
 * @function buildCoordinatesFromPath
 * @description Converts a Google Maps Path object into an array of [longitude, latitude] coordinate pairs.
 * Not currently called anywhere in the codebase.
 * @param {Object} path - Google Maps Path object
 * @memberof module:Shoreline
 * @returns {Array<Array<number>>} Array of [longitude, latitude] pairs
 */
const buildCoordinatesFromPath = (path) => {
  const coordinates = [];
  for (let i = 0; i < path.getLength(); i++) {
    const latLng = path.getAt(i);
    coordinates.push([latLng.lng(), latLng.lat()]);
  }
  return coordinates;
};

/**
 * @function performAnalysis
 * @description
 * Performs coastline analysis by processing baseline, transects, and satellite images
 * to generate shoreline statistics and visualizations.
 * 1. Creates a buffer region of interest (ROI) from the baseline.
 * 2. Generates orthogonal transects and derives shoreline data from the satellite images.
 * 3. Generates per-transect statistics and a shoreline summary.
 * 4. Pushes shoreline/transect visualizations and statistics to the map and results state.
 * @generator
 * @param {Object} params.payload - The main payload containing analysis data
 * @param {Array} params.payload.coordinatesBaseline - Array that contains baseline's coordinates
 * @param {Array} params.payload.spacing - Space between each transect in meters
 * @param {number} params.payload.extent - Transect extent in meters, it defines the buffer around the baseline
 * @param {Array} params.payload.selectedImages - Selected images chosen in the first dialog
 * @param {number} params.payload.threshold - Threshold value for shoreline detection
 * @yields {Object} Redux action objects or call effects for saga middleware
 * @see {@link deriveInChunks} - Function that processes satellite images in chunks
 * @see {@link generateColors} - Utility to generate color arrays for visualization
 * @see {@link evaluate} - Utility to evaluate Earth Engine computations
 * **Called by:
 * @see {CoastlineAnalysisSteps} - Shoreline module's analysis workflow trigger.
 * @see {AnalysisCard} - ShoreR module's analysis workflow trigger.
 * @memberof module:SHARED
 * @returns {void}
 */
export const performAnalysis = function* ({
  payload: { coordinatesBaseline, spacing, extent, selectedImages, threshold },
}) {
  const { satellite } = yield select(Selectors.getAcquisitionParameters);
  const baseline = ee.Geometry.LineString(coordinatesBaseline);
  const transects = yield call(Coastline.generateOrthogonalTransects, coordinatesBaseline, spacing, extent);
  ResultsCacheHolder.transacts = transects;
  threshold = parseFloat(threshold);
  yield put(
    Results.push('baselineData', {
      coords: coordinatesBaseline,
      baseline: baseline,
    }),
  );

  const roi = baseline.buffer(extent / 2);

  const sds = yield call(deriveInChunks, selectedImages, satellite, roi, 30, threshold, transects);
  if (!sds) return;
  ResultsCacheHolder.shorelines = sds;

  const results = yield call(Coastline.generateTransectsStatistics, transects, baseline, sds, []);

  const transectsViz = yield call(Coastline.expandHorizontally, results, 10);
  let shorelines;
  try {
    shorelines = yield call(Coastline.summaryShorelineStatistics, results, sds, roi);
  } catch (error) {
    yield put(Imagery.invalidTransectError(error.message));
    return;
  }

  ResultsCacheHolder.shorelines = sds;

  const shoreColors = generateColors(selectedImages.length, 66);

  const transectLabels = yield evaluate(results.map((f) => ee.Feature(f).get('label')));
  const transectColors = transectLabels.map((label) => Metadata.ESTEVES_LABELS[label].color);

  const shorelinesData = yield call(() => new Promise((resolve, reject) => shorelines.evaluate(resolve, reject)));
  let newFeatures = [];
  shorelinesData.features.forEach((el) => {
    let coordinates = el.geometry.coordinates;
    if (el.geometry.type === 'LinearRing') coordinates = coordinates.slice(0, coordinates.length - 1);
    let newFeature = ee.Feature(ee.Geometry.LineString(coordinates), el.properties);
    newFeatures.push(newFeature);
    return el;
  });
  shorelines = ee.FeatureCollection(newFeatures);
  yield put(Map.addEEFeature(shorelines, 'forms.map.shorelines', shoreColors, 1, 'coastlineData'));
  yield put(
    Map.addEEFeature(transectsViz, 'forms.map.transects.title', transectColors, 1, Metadata.FeatureType.TRANSECT),
  );

  const [transectData, shorelineData] = [results.getInfo(), shorelines.toList(shorelines.size())];
  yield put(Results.push('coastlineData', { transectData, shorelineData }));
  yield put(Dialog.open('coastlineEvolution'));
};

/**
 * @function handleImageSelection
 * @description Resets the previous analysis state, then opens the image selection dialog and
 * pushes the images chosen by the user to `chosenImages`.
 * @generator
 * @yields {Object} - Remove coastline data from the previous analysis (via handleRestartAnalysis)
 * @yields {Array} - Open the 'imageSelection' dialog and wait for the selected images
 * @yields {Object} - Add the selected images to chosenImages
 * @see {@link handleRestartAnalysis}
 * @see {@link openAndWait} - generator responsible for opening the dialog and waiting for a return
 * **Called by:
 * @see {CoastlineAnalysisSteps} - Shoreline module's "Select images" button.
 * @see {ShoreRAnalysisSteps} - ShoreR module's "Select images" button.
 * @memberof module:SHARED
 * @returns {void}
 */
export const handleImageSelection = function* () {
  yield* handleRestartAnalysis();
  const dates = yield call(openAndWait, 'imageSelection');
  yield put(
    Results.push(
      'chosenImages',
      dates.map((el) => el),
    ),
  );
};

/**
 * @function handleSelectShorelinesForViz
 * @description Variant of `handleImageSelection` that does NOT reset the analysis state. Intended
 * for the ShoreR flow, after the analysis has already run, so the user can pick which shorelines
 * remain visible on the map without losing the cached transects, shorelines, and statistics.
 * Not currently dispatched from any component.
 * @generator
 * @yields {Array} - Opens the 'imageSelectionShoreR' dialog and waits for the user
 * @yields {Object} - Pushes the selected dates to chosenImages
 * @see {@link openAndWait}
 * @memberof module:ShoreR
 * @returns {void}
 */
export const handleSelectShorelinesForViz = function* () {
  const dates = yield call(openAndWait, 'imageSelectionShoreR');
  if (!dates) return;
  yield put(
    Results.push(
      'chosenImages',
      dates.map((el) => el),
    ),
  );
};

/**
 * @function handleRestartAnalysis
 * @description Resets the shoreline analysis state to allow for a fresh start.
 * @generator
 * @yields {Object} - Remove coastline data from the previous analysis
 * @yields {Object} - Remove transect data from the previous analysis
 * @yields {Object} - Clear chosen images from the state
 * **Called by:
 * @see {handleImageSelection}
 * @see {CoastlineAnalysisSteps} - via the "Restart analysis" button's onClick, and the errorAnalysis effect.
 * @memberof module:SHARED
 * @returns {void}
 */
export const handleRestartAnalysis = function* () {
  yield put(Map.removeShapeGroup('coastlineData'));
  yield put(Map.removeShapeGroup(Metadata.FeatureType.TRANSECT));
  ResultsCacheHolder.shorelines = null;
  ResultsCacheHolder.transacts = null;
  yield put(Results.push('chosenImages', []));
  yield put(Results.push('coastlineData', null));
};
